import { useMemo } from 'react';
import { ElasticSearch, ElasticSearchFilterKeys } from '@interfaces/models/elasticSearch';
import { AlertCriterion, AlertCriterionKey } from '@interfaces/models/alertCriterion';
import { filtersToAlertKeysRename } from '@maps/catalog/filters-to-alert-keys-map';
import { useContextSelector } from 'use-context-selector';
import { CatalogContext } from '@context/catalog.context';
import useMarketingFiltersQuery from '@hooks/marketing-filters/use-marketing-filters-query';
import { ContentsMarketingFilter } from '@interfaces/models/contentsMarketingFilter';

const availableCriteriaKeys: AlertCriterionKey[] = [
  'path',
  'searchQuery',
  'idUniverse',
  'idBrand',
  'idPattern',
  'idColors',
  'idSeason',
  'idCondition',
  'idCategory',
  'idChildCategory',
  'idSubcategory',
  'idProduct',
  'idModel',
  'idWatchMechanism',
  'discount',
  'tags',
  'itemLocations',
];

const mergeCatLvl0AndLvl1 = (criteria: AlertCriterion[]) => {
  const categoryLvl0Crit = criteria.find((criterion) => criterion.key === 'idCategory');
  const categoryLvl1Crit = criteria.find((criterion) => criterion.key === 'idChildCategory');

  if (
    categoryLvl0Crit &&
    Array.isArray(categoryLvl0Crit.value) &&
    categoryLvl1Crit &&
    Array.isArray(categoryLvl1Crit.value)
  ) {
    const newCriteria = criteria.filter((criterion) => !['idCategory', 'idChildCategory'].includes(criterion.key));
    return [
      ...newCriteria,
      {
        key: 'idCategory',
        value: [...categoryLvl0Crit.value, ...categoryLvl1Crit.value],
      } as AlertCriterion,
    ];
  }

  return criteria;
};

interface ComputeAlertCriteria {
  filters: ElasticSearch['filters'];
  marketingFilters: ContentsMarketingFilter[];
  searchQuery: string;
}
export const computeAlertCriteria = ({
  filters,
  marketingFilters,
  searchQuery,
}: ComputeAlertCriteria): AlertCriterion[] => {
  if (!filters) {
    return [];
  }

  const doesFilterStartWith = (filters: ElasticSearch['filters'], prefix: string) =>
    Object.keys(filters).some((filter) => filter.startsWith(prefix));

  // Material alert has its own logic which we'll implement at the end
  const criteria: AlertCriterion[] = Object.keys(filters)
    .filter((filter: ElasticSearchFilterKeys) => {
      const key: AlertCriterionKey = filtersToAlertKeysRename(filter);
      return availableCriteriaKeys.includes(key);
    })
    .map((filter: keyof ElasticSearch['filters']) => {
      // Keys need to be renamed to comply with the API
      const key: AlertCriterionKey = filtersToAlertKeysRename(filter);
      const value = [];

      // Add multiple filter values under the same key
      if (Array.isArray(filters[filter])) {
        // Filter exceptions that should be sent as strings
        const stringFilters = ['discount', 'editorPicks', 'country'];

        (filters[filter] as string[]).forEach((id) => {
          const parsedId = stringFilters.includes(filter) ? id : Number(id);

          value.push(parsedId);
        });
      }

      return {
        key,
        value,
      };
    });

  // Search keywords
  if (searchQuery) {
    criteria.push({
      key: 'searchQuery',
      value: [searchQuery],
    });
  }

  // LOCAL COUNTRIES
  if (doesFilterStartWith(filters, 'localCountries')) {
    const regionCountries = marketingFilters?.find(
      (marketingFilter) => marketingFilter?.filterType === 'sustainability',
    )?.attributes?.countries;

    if (!doesFilterStartWith(filters, 'country')) {
      // add all countries of REGION selected
      criteria.push({
        key: 'itemLocations',
        value: regionCountries,
      });
    }
  }

  // Add stock filter
  if (filters.stock) {
    criteria.push({
      key: 'stock',
      value: 1,
    });
  } else {
    // Stock filter should be added even though stock is not a selected filter
    criteria.push({
      key: 'stock',
      value: 0,
    });
  }

  // Add price filters
  if (filters.price) {
    // Max price
    if (filters.price.hasOwnProperty('<=')) {
      criteria.push({
        key: 'priceMax',
        value: [filters.price['<='] / 100],
      });
    }

    // Min price
    if (filters.price.hasOwnProperty('>=')) {
      criteria.push({
        key: 'priceMin',
        value: [filters.price['>='] / 100],
      });
    }
  }

  // Add size filters
  if (doesFilterStartWith(filters, 'size')) {
    const sizeCriteria: AlertCriterion = {
      key: 'idSize',
      value: [],
    };

    Object.keys(filters).forEach((filter) => {
      if (filter.startsWith('size')) {
        // Get size id
        const sizeIds = filters[filter].map((size) => size.substring(size.indexOf('#') + 1));

        sizeIds.forEach((id) => (sizeCriteria.value as number[]).push(Number(id)));
      }
    });

    criteria.push(sizeCriteria);
  }

  // Add materials filters
  if (doesFilterStartWith(filters, 'material')) {
    const materialsCriteria: AlertCriterion = {
      key: 'idMaterials',
      value: [],
    };

    Object.keys(filters).forEach((filter) => {
      if (filter.startsWith('material')) {
        filters[filter].forEach((id) => (materialsCriteria.value as number[]).push(Number(id)));
      }
    });

    criteria.push(materialsCriteria);
  }
  return mergeCatLvl0AndLvl1(criteria);
};

export const areAlertCriteriaTooBroad = (criteria: AlertCriterion[]) => {
  // if the keys of criteria contain only one or many of these: ['idUniverse', 'itemLocations', 'stock']
  // then the criteria is too broad
  const criteriaKeys = criteria.map((criterion) => criterion.key);
  const tooBroadCriteriaKeys: AlertCriterionKey[] = ['idUniverse', 'itemLocations', 'stock'];
  return criteriaKeys.every((key) => tooBroadCriteriaKeys.includes(key));
};

const useAlertsCriteria = () => {
  const filters = useContextSelector(CatalogContext, (v) => v.filters);
  const { marketingFilters } = useMarketingFiltersQuery();
  const searchQuery = useContextSelector(CatalogContext, (v) => v.searchQuery);

  const alertCriteria = useMemo<AlertCriterion[]>(
    () => computeAlertCriteria({ filters, marketingFilters, searchQuery }),
    [filters, marketingFilters, searchQuery],
  );

  return alertCriteria;
};

export default useAlertsCriteria;
